/**
 * DigestCard.jsx — WP-A7 (docs/validation-autopilot-spec.md): the one-screen
 * build digest at the top of the launch Sign-off view.
 *
 * Renders GET /proxy/launches/<id>/digest (proxy-server/launch/digest.js). It
 * re-fetches on a timer and whenever `refreshKey` changes, so a run attached
 * while the page is open shows up WITHOUT a reload. Display only: it never
 * writes launch.signoff, never calls an LLM. Unknown coverage is shown as
 * unknown (neutral), never as a failure.
 *
 * Mount (LaunchPage.jsx, top of the Sign-off view for the selected launch):
 *   <DigestCard launchId={launch.id} refreshKey={launch.runs?.length} />
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');
const POLL_MS = 20000;
const CAP = 5;

export async function fetchDigest(launchId, { fetchImpl } = {}) {
  const f = fetchImpl || fetch;
  const res = await f(`${PROXY_API_BASE}/proxy/launches/${encodeURIComponent(launchId)}/digest`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`digest ${res.status}`);
  const body = await res.json();
  return body && body.digest ? body.digest : null;
}

const COLOR = { pass: 'success', fail: 'error', unknown: 'warning' };

/** Pure view-model so the display decisions are testable without a renderer. */
export function digestCardModel(d) {
  if (!d || !d.verdict) return null;
  const arr = (v) => (Array.isArray(v) ? v : []);
  const more = (l) => Math.max(0, l.length - CAP);
  const failing = arr(d.failingSlots);
  const unknown = arr(d.unknown);
  const items = arr(d.tickets && d.tickets.items);
  const heals = arr(d.heals);
  const ch = d.changes || {};
  const cap = d.spend && d.spend.cap;
  return {
    verdict: d.verdict.value,
    color: COLOR[d.verdict.value] || 'warning',
    line: d.verdict.line,
    since: ch.sinceBuild ? `Since last green (${ch.sinceBuild})` : 'No earlier green run to compare',
    changes: [
      ...arr(ch.criteria && ch.criteria.rows).slice(0, CAP).map((r) => `${r.label}: ${r.text}`),
      ...arr(ch.findings).slice(0, CAP).map((f) => f.message),
    ],
    failing: failing.slice(0, CAP), failingMore: more(failing),
    unknown: unknown.slice(0, CAP), unknownMore: more(unknown),
    ticketsConfigured: !!(d.tickets && d.tickets.configured),
    tickets: items.slice(0, CAP), ticketsMore: more(items),
    baseline: d.baseline && d.baseline.action === 'auto-promoted'
      ? `Auto-promoted from build ${d.baseline.fromBuild || '?'}`
      : `No baseline action${d.baseline && d.baseline.streak != null ? ` (green streak ${d.baseline.streak})` : ''}`,
    heals: heals.slice(0, CAP), healsMore: more(heals),
    spend: d.spend
      ? `${Number(d.spend.total).toLocaleString('en-US')} tokens, ${d.spend.calls} call(s)${cap ? ` — ${Math.round((d.spend.total / cap) * 100)}% of cap` : ' — no cap'}`
      : 'unavailable',
  };
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</Typography>
      {children}
    </Box>
  );
}

const Line = ({ children }) => <Typography variant="body2" sx={{ pl: 1 }}>{children}</Typography>;

export default function DigestCard({ launchId, refreshKey, pollMs = POLL_MS }) {
  const [digest, setDigest] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!launchId) return;
    try { setDigest(await fetchDigest(launchId)); setError(''); } catch (e) { setError(e.message || 'could not load digest'); }
  }, [launchId]);

  useEffect(() => {
    load();
    const t = pollMs > 0 ? setInterval(load, pollMs) : null;
    return () => { if (t) clearInterval(t); };
  }, [load, refreshKey, pollMs]);

  const m = digestCardModel(digest);
  if (!m) return error ? <Typography variant="caption" color="text.secondary" data-testid="digest-error">Build digest unavailable ({error})</Typography> : null;

  return (
    <Box data-testid="digest-card" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2, bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" color={m.color} label={m.verdict.toUpperCase()} sx={{ fontWeight: 700 }} />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.line}</Typography>
      </Stack>
      <Section title={m.since}>
        {m.changes.length ? m.changes.map((c, i) => <Line key={i}>{c}</Line>) : <Line>No changes</Line>}
      </Section>
      <Section title={`Failing slots (${m.failing.length + m.failingMore})`}>
        {m.failing.length ? m.failing.map((s, i) => (
          <Line key={i}>
            {s.text} — {s.checkId}{s.reason ? `: ${s.reason}` : ''}{' '}
            {s.links.map((l, j) => <Link key={j} href={l} sx={{ mr: 0.5 }}>req{j + 1}</Link>)}
          </Line>
        )) : <Line>None</Line>}
        {m.failingMore > 0 && <Line>…and {m.failingMore} more</Line>}
      </Section>
      <Section title={`Unknown coverage — not seen, not a failure (${m.unknown.length + m.unknownMore})`}>
        {m.unknown.length ? m.unknown.map((u, i) => <Line key={i}>{u.text} — {u.reason}</Line>) : <Line>None</Line>}
        {m.unknownMore > 0 && <Line>…and {m.unknownMore} more</Line>}
      </Section>
      <Section title="Tickets">
        {!m.ticketsConfigured ? <Line>Not configured</Line>
          : m.tickets.length ? m.tickets.map((t, i) => <Line key={i}>{t.action} {t.issueUrl ? <Link href={t.issueUrl}>{t.issueUrl}</Link> : t.fingerprint}</Line>)
            : <Line>None filed or updated for this build</Line>}
        {m.ticketsMore > 0 && <Line>…and {m.ticketsMore} more</Line>}
      </Section>
      <Section title="Baseline"><Line>{m.baseline}</Line></Section>
      <Section title={`Healed selectors needing review (${m.heals.length + m.healsMore})`}>
        {m.heals.length ? m.heals.map((h, i) => <Line key={i}>{h.key}{h.via ? ` via ${h.via}` : ''}</Line>) : <Line>None</Line>}
        {m.healsMore > 0 && <Line>…and {m.healsMore} more</Line>}
      </Section>
      <Section title="LLM spend today"><Line>{m.spend}</Line></Section>
    </Box>
  );
}
